import { ErrorMessageSection } from "./shared";

export function WarehouseContent() {
  return (
    <>
      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">How Sugargoo's Warehouse System Works</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          When you place an order on Sugargoo, the seller ships your item to Sugargoo's warehouse facility rather than directly to your international address. The warehouse serves as a centralized receiving, inspection, storage, and consolidation hub — a critical link in the Sugargoo shopping chain that offers significant advantages for international shoppers.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          Upon arrival at the warehouse, each item is logged into your account, photographed during quality inspection, weighed, and stored until you are ready to ship. This system allows you to accumulate multiple orders from different sellers over days or weeks, then ship everything together in one consolidated international parcel — dramatically reducing per-item shipping costs compared to sending each item individually.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          A <strong>Sugargoo Spreadsheet</strong> complements the warehouse system by helping you track which items have arrived, which are pending QC review, and when your storage free period expires — ensuring nothing slips through the cracks during the accumulation phase of your haul.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Warehouse Services Breakdown</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-background-300">
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Service</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Description</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-800">Cost</th>
              </tr>
            </thead>
            <tbody>
              {[
                { service: "Free Storage", desc: "Items stored for 30-90 days depending on account level", cost: "Free" },
                { service: "Quality Inspection", desc: "Photos from multiple angles, defect checks, sizing verification", cost: "Free or small fee" },
                { service: "Detailed QC", desc: "Extra photos, measurements, close-up detail shots", cost: "Small fee per item" },
                { service: "Consolidation", desc: "Combine multiple items into one shipment package", cost: "Free with shipping" },
                { service: "Package Removal", desc: "Remove shoeboxes, excess packaging to reduce weight", cost: "Usually free" },
                { service: "Rehearsal Shipping", desc: "Pre-pack and weigh for exact shipping cost quote", cost: "Small service fee" },
              ].map((row) => (
                <tr key={row.service} className="border-b border-background-200/50">
                  <td className="py-3 px-4 font-medium text-foreground-800">{row.service}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.desc}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.cost === "Free" ? "bg-primary-100 text-primary-800" : "bg-secondary-100 text-secondary-800"}`}>{row.cost}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Smart Warehouse Tips for Sugargoo Shoppers</h2>
        <div className="space-y-4">
          {[
            { title: "Plan Your Storage Window", desc: "Know exactly when your free storage period expires. Set calendar reminders to avoid unexpected storage fees that can quietly accumulate across multiple items." },
            { title: "Review QC Photos Immediately", desc: "Inspect quality check photos as soon as they appear in your account. If an item has visible defects, initiate a return or exchange while it is still within the warehouse return window." },
            { title: "Batch Ship Strategically", desc: "Group items by shipping urgency. Ship time-sensitive items via express methods immediately, while accumulating non-urgent items for a larger consolidated sea freight shipment." },
            { title: "Request Weight Data", desc: "After QC, note the weight of each item. Use this data in your Sugargoo Spreadsheet to estimate total shipping costs before committing to a carrier method." },
          ].map((tip, i) => (
            <div key={i} className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/60">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-primary-600"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1">{tip.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <ErrorMessageSection />
    </>
  );
}